import { useState } from "react";
import { Navigate } from "react-router-dom";
import { PhoneCall } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const result = await login(email.trim(), password);
    if (!result.ok) setError(result.error);
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <div className="hidden w-1/2 flex-col justify-between bg-slate-950 p-10 lg:flex">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/25 bg-white/5">
            <PhoneCall className="h-4.5 w-4.5 text-white" strokeWidth={2.2} />
          </span>
          <span className="font-display text-2xl font-bold tracking-tight text-white">ai2Dial</span>
        </div>
        <div className="max-w-md">
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">
            Outbound AI voice campaign control room
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            Launch multilingual AI sales campaigns, watch calls live, review transcripts and costs,
            and keep every do-not-call request enforced automatically.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 text-slate-300">
            {[
              ["Providers", "Adapter based"],
              ["Compliance", "Global DNC"],
              ["Costs", "Budget guards"],
            ].map(([k, v], i) => (
              <div key={k} className={["rounded-xl border border-indigo-500/40 bg-indigo-500/10 px-3 py-2.5", "rounded-xl border border-teal-500/40 bg-teal-500/10 px-3 py-2.5", "rounded-xl border border-orange-500/40 bg-orange-500/10 px-3 py-2.5"][i]}>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{k}</p>
                <p className="text-sm font-semibold text-white">{v}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-500">Live calling through Vapi and your SIP carrier.</p>
      </div>

      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Enter your email and password to continue.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error ? (
              <p data-testid="login-error" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </p>
            ) : null}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="h-10 w-full bg-gradient-to-r from-indigo-600 to-violet-600 font-semibold shadow-md shadow-indigo-600/30 transition-all duration-200 hover:from-indigo-700 hover:to-violet-700 active:scale-[0.98]"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}